/**
 * API Service
 *
 * Centralized service for making authenticated API calls to the backend.
 * Automatically includes the Auth0 access token in requests.
 *
 * Usage:
 * ```typescript
 * import { apiClient, publicApiClient } from '@/services/api';
 *
 * // Authenticated GET request
 * const data = await apiClient.get('/api/v1/endpoint');
 *
 * // Public GET request (no auth headers)
 * const publicData = await publicApiClient.get('/api/v1/public/endpoint');
 * ```
 */

// Base URL for your backend API
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";

/**
 * Token getter function type
 * This will be set by the useApi hook
 */
let getToken: (() => Promise<string>) | null = null;

/**
 * Logout handler function type
 * This will be set by the useApi hook to trigger logout on persistent auth errors
 */
let onAuthError: (() => void) | null = null;

/**
 * Set the token getter function
 * Called by useApi hook to provide Auth0 token access
 */
export function setTokenGetter(tokenGetter: () => Promise<string>) {
  getToken = tokenGetter;
}

/**
 * Set the auth error handler (logout function)
 * Called by useApi hook to handle persistent auth errors
 */
export function setAuthErrorHandler(handler: () => void) {
  onAuthError = handler;
}

/**
 * Clear the token getter when the user logs out or auth context changes
 */
export function clearTokenGetter() {
  getToken = null;
}

/**
 * Clear the auth error handler
 */
export function clearAuthErrorHandler() {
  onAuthError = null;
}

/**
 * Check if the token getter is currently set
 * Used to verify initialization state
 */
export function isTokenGetterSet(): boolean {
  return getToken !== null;
}

/**
 * Make an authenticated API request with automatic token refresh on 401
 */
async function authenticatedFetch(
  url: string,
  options: RequestInit = {},
  retryCount = 0
): Promise<Response> {
  const headers = new Headers(options.headers);

  // Fail fast if token getter is not set - this indicates a race condition
  if (!getToken) {
    const errorMsg =
      "Token getter not initialized. Ensure useApi() hook is called in a component wrapped by ProtectedRoute.";
    console.error(errorMsg);
    throw new Error("Not authenticated");
  }

  // Add Auth0 token
  try {
    const token = await getToken();
    if (!token) {
      throw new Error("Token getter returned empty token");
    }
    headers.set("Authorization", `Bearer ${token}`);
  } catch (error) {
    console.error("Failed to get access token:", error);
    if (onAuthError) {
      console.log("Triggering logout due to token getter failure");
      onAuthError();
    }
    throw new Error("Authentication failed");
  }

  // Set content type if not already set and body is present
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // Make the request
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  });

  if (response.status === 401 && retryCount === 0 && getToken) {
    console.log("Got 401, attempting to refresh token and retry...");
    try {
      const newToken = await getToken();
      if (newToken) {
        headers.set("Authorization", `Bearer ${newToken}`);
        return authenticatedFetch(
          url,
          {
            ...options,
            headers,
          },
          retryCount + 1
        );
      }
    } catch (error) {
      console.error(
        "Failed to refresh access token: Unable to obtain new authentication credentials",
        error
      );
    }
  }

  // Handle other errors
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.detail || `API Error: ${response.statusText}`;

    if (response.status === 401) {
      console.error("Authentication failed:", errorMessage);
      // Trigger logout on persistent auth errors
      if (onAuthError) {
        console.log("Triggering logout due to persistent auth error");
        onAuthError();
      }
    }

    throw new ApiError(errorMessage, response.status, errorData.detail);
  }

  return response;
}

/**
 * Custom error class that preserves HTTP status codes
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public detail?: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Make a public API request without authentication headers
 * Use this for public endpoints that should not include auth tokens
 */
async function publicFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const headers = new Headers(options.headers);

  // Set content type if not already set and body is present
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // Make the request without auth headers
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  });

  // Handle errors
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.detail || `API Error: ${response.statusText}`;
    throw new ApiError(errorMessage, response.status, errorData.detail);
  }

  return response;
}

/**
 * API Client with common HTTP methods
 */
export const apiClient = {
  /**
   * GET request
   */
  async get<T = unknown>(url: string, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(url, {
      ...options,
      method: "GET",
    });
    return response.json();
  },

  /**
   * POST request
   */
  async post<T = unknown>(url: string, data?: unknown, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(url, {
      ...options,
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
    return response.json();
  },

  /**
   * PUT request
   */
  async put<T = unknown>(url: string, data?: unknown, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(url, {
      ...options,
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
    return response.json();
  },

  /**
   * PATCH request
   */
  async patch<T = unknown>(url: string, data?: unknown, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(url, {
      ...options,
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
    return response.json();
  },

  /**
   * DELETE request
   * Handles 204 No Content responses properly (no body to parse)
   */
  async delete<T = void>(url: string, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(url, {
      ...options,
      method: "DELETE",
    });
    // 204 No Content has no body - return undefined
    if (response.status === 204) {
      return undefined as T;
    }
    return response.json();
  },
};

/**
 * Public API Client - for endpoints that don't require authentication
 * Use this for public share access and other unauthenticated endpoints
 */
export const publicApiClient = {
  /**
   * GET request (no auth)
   */
  async get<T = unknown>(url: string, options?: RequestInit): Promise<T> {
    const response = await publicFetch(url, {
      ...options,
      method: "GET",
    });
    return response.json();
  },

  /**
   * POST request (no auth)
   */
  async post<T = unknown>(url: string, data?: unknown, options?: RequestInit): Promise<T> {
    const response = await publicFetch(url, {
      ...options,
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
    return response.json();
  },
};
